import { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api2b.pro';

/**
 * Custom hook to fetch logo data from the API.
 * Deduplicates the 14+ duplicate /api/logo fetch calls across the app.
 * Returns { logo, mobileLogo, favicon, loaderLogo, siteName, loading }
 */
const logoCache = new Map();

function useLogo(options = {}) {
  const { fields = 'all' } = options;
  const [logoData, setLogoData] = useState({
    logo: null,
    mobileLogo: null,
    favicon: null,
    loaderLogo: null,
    siteName: null,
    loading: true,
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const origin = window.location.hostname.replace(/^www\./, '');
    const cacheKey = `logo_${origin}`;

    // Return cached data if available
    if (logoCache.has(cacheKey)) {
      setLogoData(prev => ({ ...prev, ...logoCache.get(cacheKey), loading: false }));
      return;
    }

    const controller = new AbortController();

    const fetchLogo = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/logo?origin=${encodeURIComponent(origin)}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error('Failed to fetch logo');
        const data = await res.json();
        const item = Array.isArray(data) ? data[0] : data;

        const buildUrl = (path) => {
          if (!path) return null;
          return path.startsWith('http') ? path : `${API_URL}/storage/${path}`;
        };

        const result = {
          logo: buildUrl(item?.logo),
          mobileLogo: buildUrl(item?.mobile_logo),
          favicon: buildUrl(item?.favicon),
          loaderLogo: buildUrl(item?.loader_logo),
          siteName: item?.site_name || null,
          raw: item,
        };

        logoCache.set(cacheKey, result);

        if (mountedRef.current) {
          setLogoData(prev => ({ ...prev, ...result, loading: false }));
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          if (mountedRef.current) {
            setLogoData(prev => ({ ...prev, loading: false }));
          }
        }
      }
    };

    fetchLogo();

    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, []);

  return logoData;
}

export default useLogo;
